import { Fragment, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { api } from "helpers/axios";
import { empty, errorMessage, formatDateTime, scrollToTop } from "helpers/common";
import { detectCsvDelimiter } from "helpers/importFieldMatching";

import { FilePond, registerPlugin } from 'react-filepond';
import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type';
import FilePondPluginFileEncode from 'filepond-plugin-file-encode';

import 'filepond/dist/filepond.min.css';

import ErrorAlert from "components/ui/ErrorAlert";
import ImportPeriodWarningModal from "components/ui/ImportPeriodWarningModal";
import { isCSVFile } from "helpers/file";
import useImportPeriodGuard from "hooks/useImportPeriodGuard";
import { useTranslation } from "translation/useTranslation";
import { Button, Input, NativeSelect } from "components/ui";

registerPlugin(
  FilePondPluginFileValidateType,
  FilePondPluginFileEncode
);

export default function EmployeeImportWizard({ setMode, handleImportClick }) {
  const { translate, editableTranslate, editableTranslateString } = useTranslation();

  const [page, setPage] = useState(1);

  const [files, setFiles] = useState([]);

  const [isCsv, setIsCsv] = useState(false);
  const [delimiter, setDelimiter] = useState(',');
  const [isAutoDetected, setIsAutoDetected] = useState(false);
  const [loading, setLoading] = useState(false);

  const [fetchingInitialData, setFetchingInitialData] = useState(false);

  const [error, setError] = useState('');
  const [availableScopeFields, setAvailableScopeFields] = useState([]);

  const [selectedScopeFieldsFile, setSelectedScopeFieldsFile] = useState([]);
  const [selectedScopeFieldsSource, setSelectedScopeFieldsSource] = useState([]);

  const [customDelimiter, setCustomDelimiter] = useState('');

  const [availableSources, setAvailableSources] = useState([]);
  const [selectedSource, setSelectedSource] = useState('');

  const {
    checkingPeriods,
    handleCancelPeriodWarning,
    handleConfirmPeriodWarning,
    runWithPeriodGuard,
    warningState,
  } = useImportPeriodGuard(setError);

  const hasExternalSource = useMemo(() => availableSources.length > 0, [availableSources]);

  const fetchAvailableSources = async () => {
    try {
      const { data, status } = await api.get('/employee-import-sources');
      if (status === 200) {
        setAvailableSources(data.data);
      }
    } catch (error) {
      setError(errorMessage(error, 'fetchAvailableSources'));
      console.error('Error fetching available sources:', error);
    }
  };

  const fetchAvailableFields = async () => {
    try {
      const { data, status } = await api.get(`/employee-imports/available-fields`);

      if (status === 200) {
        setAvailableScopeFields(data.data);
      }
    } catch (error) {
      setError(errorMessage(error, 'fetchAvailableFields'));
    }
  }

  // all required fields straight away put into selectedScopeFields
  useEffect(() => {
    if (availableScopeFields.length > 0) {
      const initialSelectedFields = availableScopeFields.filter(f => f.required);
      setSelectedScopeFieldsFile(initialSelectedFields);
      setSelectedScopeFieldsSource(initialSelectedFields);
    }
  }, [availableScopeFields]);

  useEffect(() => {
    (async () => {
      setFetchingInitialData(true);
      await fetchAvailableSources();
      await fetchAvailableFields();
      setFetchingInitialData(false);
    })();
  }, []);

  useEffect(() => {
    if (files.length > 0 && isCSVFile(files[0])) {
      setIsCsv(true);
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target.result;
        const detected = detectCsvDelimiter(content);
        const knownDelimiters = [',', ';', '\t', '|'];
        if (knownDelimiters.includes(detected)) {
          setDelimiter(detected);
          setCustomDelimiter('');
        } else {
          setDelimiter('custom');
          setCustomDelimiter(detected);
        }
        setIsAutoDetected(true);
      };
      reader.readAsText(files[0].file.slice(0, 1024));
    } else {
      setIsCsv(false);
      setIsAutoDetected(false);
      setDelimiter(',');
      setCustomDelimiter('');
    }
  }, [files]);

  useEffect(() => {
    if (error) scrollToTop();
  }, [error]);

  const validateFiles = async () => {
    setError('');
    // always expect only one file
    if (files.length < 1) {
      setError(editableTranslate('user_import.no_file_uploaded', 'Keine Datei hochgeladen. Bitte laden Sie eine CSV- oder Excel-Datei hoch.'));
      setFiles([]);
      return;
    }

    const fileItem = files[0];
    const file = fileItem.file;

    // validate file type
    const validTypes = ['text/csv', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.ms-excel'];
    if (!validTypes.includes(file.type)) {
      setError(editableTranslate('user_import.invalid_file_type', 'Ungültiger Dateityp. Bitte laden Sie eine CSV- oder Excel-Datei hoch.'));
      setFiles([]);
      return;
    }

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('import_type', 'file');
      formData.append('delimiter', delimiter === 'custom' ? customDelimiter : delimiter);

      const { data, status } = await api.post('/employee-imports/validate-file', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (status === 200) {
        setError('');
        return true;
      }
    } catch (error) {
      setError(errorMessage(error, 'validateFiles'));
      setFiles([]);
    }

    return false;
  };

  const handleSourceSelection = async (hashId) => {
    if (loading || checkingPeriods) return;

    if (selectedSource === hashId) {
      await handleCreateImport('source', hashId);
      return;
    }

    setSelectedSource(hashId);
    await handleCreateImport('source', hashId);
  };

  const handleSaveImportScope = async (type) => {
    try {
      const { data, status } = await api.post(`/employee-imports/scopes`, {
        fields: (type === 'file' ? selectedScopeFieldsFile.map(f => f.key) : selectedScopeFieldsSource.map(f => f.key)),
      });
      if (status === 201) {
        setError('');
        return data.data.import_scope_id;
      }
    } catch (error) {
      setError(errorMessage(error, 'handleSaveImportScope'));
    }
  };

  const gotoPage = (page) => {
    setError('');
    setPage(page);
  };

  const handleFileDropped = async (e = null) => {
    e?.preventDefault();
    if (!files.length && !delimiter) {
      setError(editableTranslate('user_import.file_and_delimiter_required', 'Bitte laden Sie eine Datei hoch und wählen Sie ein Trennzeichen aus.'));
      return;
    }

    const validated = await validateFiles();

    if (!validated) {
      return;
    }

    await handleCreateImport('file');
  }

  const handleCreateImport = async (type = 'file', sourceHashId = '') => {
    await runWithPeriodGuard(() => onSubmit(type, sourceHashId));
  };

  const onSubmit = async (type = 'file', sourceHashId = '') => {
    if ((type === 'file' && selectedScopeFieldsFile.length === 0) || (type === 'source' && selectedScopeFieldsSource.length === 0)) {
      setError(editableTranslate('user_import.no_fields_selected', 'Bitte wählen Sie mindestens ein zu importierendes Feld aus.'));
      return;
    }

    setLoading(true);

    const formData = new FormData();

    // save import scope. and get importScopeHashId.
    let importScopeHashId = await handleSaveImportScope(type);
    if (!importScopeHashId) {
      setLoading(false);
      return;
    }

    formData.append("import_scope_hash_id", importScopeHashId);
    formData.append("import_type", type);

    if (type === 'file') {
      files
        .map((item) => item.file)
        .forEach((file) => formData.append("file", file));

      formData.append("delimiter", delimiter === 'custom' ? customDelimiter : delimiter);
    } else if (type === 'source') {
      const activeSourceHashId = sourceHashId || selectedSource;

      if (!activeSourceHashId) {
        setError(editableTranslate('user_import.select_source', 'Bitte wählen Sie eine Quelle für den Beschäftigtenimport aus.'));
        setLoading(false);
        return;
      }

      formData.append("employee_import_source_hash_id", activeSourceHashId);
    }

    try {
      const { data, status } = await api.post('/employee-imports', formData, { headers: { 'Content-Type': 'multipart/form-data' } });

      if (status === 200) {
        console.log('Import created successfully:', data);
        // redirect to UserImportForm with import ID using navigate
        handleImportClick(data);
      }
    } catch (err) {
      console.error('Error creating import:', err);
      setError(errorMessage(err, 'onSubmit'));
    }

    setLoading(false);
  };

  return (
    <>
      <div className="mt-4">
        {
          error && <div className="mb-3">
            <ErrorAlert error={error} onClose={() => setError(null)} />
          </div>
        }

        {
          page === 1 && <>
            <h4 className="mb-1">{editableTranslate('user_import.import_wizard.new_import_title')}</h4>
            <div className="mb-3">{editableTranslate('user_import.import_wizard.new_import_description')}</div>

            {
              fetchingInitialData && <div className="text-center my-5" data-testid="loading">
                <div className="spinner-border" role="status">
                  <span className="sr-only">Wird geladen...</span>
                </div>
              </div>
            }
            {
              !fetchingInitialData && <>
                {
                  !hasExternalSource && <div className="alert alert-info">
                    {
                      editableTranslate('user_import.import_wizard.no_external_source_info', 'Neben dem Import auf CSV / Excel Dateien bitten wir ausserdem das anlegen von “Externen Datenquellen” an; diese können dann auch automatisch aktualisiert werden. Externe Datenquellen können von Ihrer IT angelegt werden')
                    }
                  </div>
                }
                {
                  hasExternalSource && <>
                    <div>
                      <div className="d-flex">
                        <div className="flex-fill">
                          <h4 className="mb-1">{editableTranslate('user_import.import_wizard.available_sources', 'Externe Datenquellen')}</h4>
                          <div className="mb-3">{editableTranslate('user_import.import_wizard.available_sources_description')}</div>
                          {
                            availableSources.length < 1 && <div className="text-muted">
                              {editableTranslate('user_import.import_wizard.no_sources', 'Es sind noch keine Quellen für den Beschäftigtenimport verfügbar.')}
                            </div>
                          }
                          {
                            availableSources.length > 0 && <div className={`row row-cols-4`}>
                              {
                                availableSources.map((source, i) => <div key={i} className={`col ${i > 3 ? 'mt-3' : ''}`}>
                                  <div className={`card-box ${selectedSource === source.hash_id ? 'selected' : ''}`}>
                                    <div className="mb-3">
                                      <div className="text2-color">
                                        {editableTranslate('user_import.import_wizard.data_source', 'Datenquelle')}
                                      </div>
                                      <div className="font-weight-bold">
                                        <span>{source.source_type === 'sftp' ? 'SFTP' : 'Active directory'}</span> - {source.name}
                                      </div>
                                    </div>
                                    <div className="mb-3">
                                      <div className="text2-color">
                                        {editableTranslate('user_import.import_wizard.source_description', 'Beschreibung')}
                                      </div>
                                      <div className="font-weight-bold">
                                        {source.description || '-'}
                                      </div>
                                    </div>
                                    <div data-testid={`select-source-${source.hash_id}`} onClick={() => handleSourceSelection(source.hash_id)} className="text-center text-primary font-weight-bold mt-3 cursor-pointer">
                                      {
                                        selectedSource === source.hash_id && (loading || checkingPeriods) && <div className="spinner-border spinner-border-sm mr-2" role="status">
                                          <span className="sr-only">Wird geladen...</span>
                                        </div>
                                      }
                                      {editableTranslate('user_import.import_wizard.use_this_source', 'Verwenden')}
                                    </div>
                                  </div>
                                </div>)
                              }
                            </div>
                          }
                        </div>
                      </div>
                      <div className="text-center mt-4">
                        <Button variant="secondary" onClick={() => setMode('view')} data-testid="cancel-button" className="mr-2">{editableTranslate('button.cancel')}</Button>
                      </div>
                    </div>

                    <div className="import-or-divider my-5">
                      <span className="import-or-text">{editableTranslate('user_import.import_wizard.or', 'Or')}</span>
                    </div>
                  </>
                }

                <div>
                  <form>
                    <div className="d-flex">
                      <div className={`flex-fill import-file-input ${files?.length > 0 ? 'has-file' : ''}`}>
                        {
                          hasExternalSource && <>
                            <h4 className="mb-1">{editableTranslate('user_import.import_wizard.file_upload', 'Aus Datei importieren')}</h4>
                            <div className="mb-3">{editableTranslate('user_import.import_wizard.file_upload_description')}</div>
                          </>
                        }

                        <FilePond
                          name="file"
                          files={files}
                          acceptedFileTypes={['text/csv', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.ms-excel']}
                          onupdatefiles={setFiles}
                          instantUpload={false}
                          allowFileEncode={true}
                          allowMultiple={false}
                          required={true}
                          credits={false}
                          storeAsFile={true}
                          labelIdle={editableTranslateString('user_import.drop_or_choose_file', 'Dateien hier ablegen oder zum Hochladen anklicken')}
                        />

                        {
                          isCsv && <>
                            <div className="form-group text-center">
                              {isAutoDetected && (
                                <div className="mb-2">
                                  <div className="alert alert-info d-inline-block mb-0 py-1 px-2">
                                    <small>{editableTranslate('user_import.delimiter_auto_detected', 'Automatische Trennzeichenerkennung eingeschaltet. Bei Bedarf manuell wechseln.')}</small>
                                  </div>
                                </div>
                              )}
                              <div>
                                <label className="peb-field__label d-inline-block mr-2" htmlFor="import-delimiter">
                                  {editableTranslate('user_import.delimiter', 'Trennzeichen')}
                                </label>
                                <div className="d-inline-block">
                                  <div className="input-group">
                                    <div className="input-group-prepend">
                                      <NativeSelect data-testid="delimiter-select" id="import-delimiter" value={delimiter} onChange={(e) => { setDelimiter(e.target.value); setIsAutoDetected(false); }}>
                                        <option value=",">{translate('user_import.delimiter.comma', 'Komma')} (,)</option>
                                        <option value=";">{translate('user_import.delimiter.semicolon', 'Semikolon')} (;)</option>
                                        <option value="&#9;">{translate('user_import.delimiter.tab', 'Tabulator')}</option>
                                        <option value="|">{translate('user_import.delimiter.pipe', 'Senkrechter Strich')} (|)</option>
                                        <option value="custom">{translate('user_import.delimiter.custom', 'Benutzerdefiniert')}</option>
                                      </NativeSelect>
                                    </div>
                                    {
                                      delimiter === 'custom' && <Input data-testid="custom-delimiter-input" type="text" className="max-w-100" autoFocus value={customDelimiter} onChange={(e) => setCustomDelimiter(e.target.value)} />
                                    }
                                  </div>
                                </div>
                              </div>
                            </div>
                          </>
                        }
                      </div>
                    </div>
                  </form>

                  <div className="text-center mt-1">
                    <Button variant="secondary" onClick={() => setMode('view')} data-testid="cancel-button" className="mr-2">{editableTranslate('button.cancel')}</Button>
                    <Button variant="primary" onClick={handleFileDropped} loading={loading || checkingPeriods} disabled={!files || files.length < 1} data-testid="next-button">{editableTranslate('button.next')}</Button>
                  </div>
                </div>
              </>
            }
          </>
        }
      </div>
      <ImportPeriodWarningModal
        show={warningState.show}
        isLoading={loading}
        title={editableTranslate('user_import.period_warning_title', 'Wichtiger Perioden-Hinweis')}
        warningText={editableTranslate('user_import.period_warning_body', 'Dieser Import wird in der aktuell ausgewaehlten Periode erstellt.')}
        currentPeriodLabel={editableTranslate('user_import.period_warning_current_period', 'Aktuelle Periode:')}
        periodName={warningState.activePeriodName}
        detailText={editableTranslate('user_import.period_warning_detail', 'Falls dies nicht die gewuenschte Zielperiode ist, wechseln Sie zuerst die Periode und starten Sie den Import danach erneut.')}
        cancelLabel={editableTranslate('button.cancel', 'Abbrechen')}
        continueLabel={editableTranslate('button.continue', 'Fortfahren')}
        onCancel={handleCancelPeriodWarning}
        onContinue={handleConfirmPeriodWarning}
      />
    </>
  );
}
